import { httpsCallable } from "firebase/functions";
import * as React from "react";
import {
    allAuthenticatedUsers,
    markEmailAsDefault,
} from "../../../../../store/calendar";
import moment from "../../../../../utils/app_moment";
import { FormCheckbox } from "../../../bits/FormCheckbox/FormCheckbox";
import { DateFormats } from "intake_shared/src/types";
import { fxs } from "../../../../../utils/firestore";
import styles from "./AuthenticateGoogleCalendar.module.scss";
import { Button } from "../../../bits/Button/Button";
import Table from "../../../bits/Table/Table";
import Alert from "../../../bits/Alert/Alert";

interface ListProps {
    email: string;
    isDefault: boolean;
    dateConnected: Date;
}

export default function AuthenticateGoogleCalendar() {
    const [display, setDisplay] = React.useState<{
        message: string;
        status: "info" | "success" | "warning" | null;
    }>({ message: "", status: null });

    const [list, setList] = React.useState<ListProps[]>([]);

    const exportAuthenticateUserFunction = httpsCallable(
        fxs,
        "httpFunctionAuthenticateClientForCalendar"
    );

    const fetchAuthenticatedList = async () => {
        const result = await allAuthenticatedUsers();
        setList(result);
    };

    React.useEffect(() => {
        fetchAuthenticatedList();
    }, []);

    const handleMarkAsDefault = async (email: string, defaultOption: boolean) => {
        try {
            const result = await markEmailAsDefault(email, defaultOption);
            if (result?.success) {
                setDisplay({
                    message: result.message,
                    status: "success",
                });
                fetchAuthenticatedList();
            }
        } catch (error) {
            console.error("Error updating default email:", error);
            setDisplay({
                message: `Failed to update, please contact your administrator.`,
                status: "warning",
            });
        }
    };

    const columns = [
        {
            Header: "Authenticated Emails",
            id: "authenticated",
            accessor: (row: ListProps) => <span>{row?.email}</span>,
        },
        {
            Header: "Date Connected",
            id: "date_connected",
            accessor: (row: ListProps) => (
                <span>
                    {moment((row?.dateConnected as any)?.toDate())?.format(
                        DateFormats.DAY_MONTH_YEAR_FORMAT
                    )}
                </span>
            ),
        },
        {
            Header: "Is Default",
            id: "is-default",
            accessor: (row: ListProps) => (
                <span>{row?.isDefault ? "True" : "False"}</span>
            ),
        },
        {
            Header: "",
            id: "mark_as_default",
            accessor: (row: ListProps) => (
                <span>
                    <FormCheckbox
                        label="Mark as Default"
                        checked={row.isDefault || false}
                        onChange={() => {
                            handleMarkAsDefault(row.email, !row.isDefault);
                        }}
                        noBorder
                        labelClass={styles.checkboxLabel}
                    />
                </span>
            ),
        },
    ];
    return (
        <div className={styles.authenticateGoogleCalendar}>
            <div className={styles.header}>
                <p className={styles.headerTitle}>Authenticate Google Calendar</p>
                <Button
                    onClick={async () => {
                        try {
                            const result = await exportAuthenticateUserFunction();
                            const data = result?.data as any;

                            if (data?.success) {
                                setDisplay({
                                    message: "Authenticated Successfully",
                                    status: "success",
                                });
                                window.open(data.url, "_blank");
                            } else {
                                setDisplay({
                                    message:
                                        data?.message ||
                                        "Failed to authenticate client, please contact your administrator.",
                                    status: "warning",
                                });
                            }
                        } catch (ex) {
                            setDisplay({
                                message:
                                    "An unexpected error occurred while trying to authenticate.",
                                status: "warning",
                            });
                        }
                    }}
                >
                    Connect Google Calendar
                </Button>
            </div>
            {display.status ? (
                <Alert type={display.status}>{display.message}</Alert>
            ) : null}
            {list.length && (
                <Table columns={columns} data={list} allowSelect={false} />
            )}
        </div>
    );
}
